import React, { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";
import { Sidebar } from "@/components/Sidebar";
import { DashboardPanel } from "@/components/DashboardPanel";
import { MilestonesPanel } from "@/components/MilestonesPanel";
import { OverviewPanel } from "@/components/OverviewPanel";
import { StakeholdersPanel } from "@/components/StakeholdersPanel";
import { PainPointsPanel } from "@/components/PainPointsPanel";
import { AsIsProcessFlow } from "@/components/AsIsProcessFlow";
import SharedQuadChart from "@/components/SharedQuadChart";
import { AsIsFieldMapping } from "@/components/AsIsFieldMapping";
import { ToBeProcessFlow } from "@/components/ToBeProcessFlow";
import { ToBeFieldMapping } from "@/components/ToBeFieldMapping";
import { RequirementsPanel } from "@/components/RequirementsPanel";
import { UATPanel } from "@/components/UATPanel";
import { GoNoGoPanel } from "@/components/GoNoGoPanel";
import { StakeholderFeedbackPanel } from "@/components/StakeholderFeedbackPanel";
import { QuadChartPanel } from "@/components/QuadChartPanel";
import { AdminPanel } from "@/components/AdminPanel";
import { MyProductsPanel } from "@/components/MyProductsPanel";
import { CalendarPanel } from "@/components/CalendarPanel";
import { ProfileSettings } from "@/components/ProfileSettings";
import { GoalsPanel } from "@/components/GoalsPanel";
import { ReportsPage } from "@/components/ReportsPage";
import { TrackingPanel } from "@/components/TrackingPanel";
import { AppHeader } from "@/components/AppHeader";
import ChangeLogPage from "@/components/ChangeLogPage";
import GoToMarketPage from "@/components/GoToMarketPage";
import { NotificationPopupModal } from "@/components/NotificationPopupModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileDown, FileText, X, Lock, Loader2, Eye, EyeOff } from "lucide-react";

const panels = {
  overview: OverviewPanel,
  dashboard: DashboardPanel,
  milestones: MilestonesPanel,
  stakeholders: StakeholdersPanel,
  painpoints: PainPointsPanel,
  "asis-flow": AsIsProcessFlow,
  "asis-mapping": AsIsFieldMapping,
  "tobe-flow": ToBeProcessFlow,
  "tobe-mapping": ToBeFieldMapping,
  "tobe-enhancements": RequirementsPanel,
  tracking: TrackingPanel,
  uat: UATPanel,
  gonogo: GoNoGoPanel,
  feedback: StakeholderFeedbackPanel,
  quadchart: QuadChartPanel,
  goals: GoalsPanel,
  reports: ReportsPage,
  admin: AdminPanel,
  myproducts: MyProductsPanel,
  calendar: CalendarPanel,
  settings: ProfileSettings,
  changelog: ChangeLogPage,
  gtm: GoToMarketPage,
};

const LoginPage = () => {
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(username, password);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid username or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] flex items-center justify-center" data-testid="login-page">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMDJjM2MiIGZpbGwtb3BhY2l0eT0iMC4zIj48cGF0aCBkPSJNMzYgMzRjMC0yIDItNCAyLTRzLTItMi00LTJjLTItMi00LTItNC0ydjRjMCAyIDIgNCAyIDRzMi0yIDQtMmMyIDAgNCAwIDQgMHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30" />
      <div className="w-full max-w-sm mx-4 relative z-10">
        <div className="text-center mb-8">
          <img src="/usahs-logo.png" alt="University of St. Augustine for Health Sciences" className="h-24 mx-auto mb-4 object-contain" data-testid="login-logo" />
          <h1 className="text-2xl font-bold text-white font-['Outfit'] tracking-tight">Product Builder</h1>
          <p className="text-sm text-slate-400 mt-1">Product Lifecycle Management</p>
        </div>
        <div className="bg-white rounded-2xl shadow-2xl p-7 ring-1 ring-black/5">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#00D4C8] to-[#39D98A] flex items-center justify-center">
              <Lock className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-sm font-semibold text-slate-700">Sign in to continue</h2>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-2 block uppercase tracking-wider">Username</label>
              <Input value={username} onChange={(e) => { setUsername(e.target.value); setError(""); }} placeholder="Enter username" autoFocus className="h-11 rounded-xl" data-testid="login-username" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-2 block uppercase tracking-wider">Password</label>
              <div className="relative">
                <Input type={showPassword ? "text" : "password"} value={password} onChange={(e) => { setPassword(e.target.value); setError(""); }} placeholder="Enter password" className="h-11 rounded-xl pr-10" data-testid="login-password" />
                <button type="button" onClick={() => setShowPassword(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors" data-testid="toggle-password" tabIndex={-1}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {error && <p className="text-xs text-red-500 font-medium bg-red-50 p-2.5 rounded-lg" data-testid="login-error">{error}</p>}
            <Button type="submit" className="w-full h-11 rounded-xl bg-gradient-to-r from-[#0F172A] to-slate-800 hover:from-slate-800 hover:to-[#0F172A] text-white font-semibold shadow-lg transition-all duration-300 hover:shadow-xl" disabled={loading} data-testid="login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Sign In
            </Button>
          </form>
        </div>
        <p className="text-center text-xs text-slate-500 mt-6">University of St. Augustine for Health Sciences</p>
      </div>
    </div>
  );
};

function MainApp() {
  const { user, loading, token, selectedProduct, selectProduct, isAdmin } = useAuth();
  const { setDark } = useTheme();
  const [activePanel, setActivePanel] = useState("myproducts");
  const [highlightTarget, setHighlightTarget] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const prevUserRef = React.useRef(null);

  // Reset to My Products page on fresh login
  useEffect(() => {
    if (user && !prevUserRef.current) {
      setActivePanel("myproducts");
    }
    prevUserRef.current = user;
  }, [user]);

  // Load user's theme preference from backend after login
  useEffect(() => {
    if (!user || !token) return;
    const loadThemePreference = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/preferences`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const theme = res.data?.theme;
        if (theme === 'dark') setDark(true);
        else if (theme === 'light') setDark(false);
        else if (theme === 'system') {
          setDark(window.matchMedia('(prefers-color-scheme: dark)').matches);
        }
      } catch (err) {
        // Preferences not set yet, keep current theme
      }
    };
    loadThemePreference();
  }, [user, token, setDark]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00D4C8]" />
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  const handleNavigate = (panel, targetId) => {
    if (panel !== 'myproducts' && panel !== 'admin' && panel !== 'calendar' && panel !== 'settings' && panel !== 'reports' && panel !== 'changelog' && !selectedProduct) {
      setActivePanel('myproducts');
    } else {
      setActivePanel(panel);
    }
    if (targetId) {
      setHighlightTarget(targetId);
      setTimeout(() => setHighlightTarget(null), 3000);
    }
    setMobileMenuOpen(false);
  };

  const handleSelectProduct = (product) => {
    selectProduct(product);
    setActivePanel('dashboard');
  };

  const ActiveComponent = panels[activePanel];

  // Pass props to MyProductsPanel
  const componentProps = activePanel === 'myproducts' 
    ? { onSelectProduct: handleSelectProduct } 
    : activePanel === 'dashboard' 
    ? { onNavigate: handleNavigate }
    : activePanel === 'tracking'
    ? { onNavigate: handleNavigate, highlightTarget }
    : activePanel === 'tobe-enhancements'
    ? { onNavigate: handleNavigate, highlightTarget }
    : {};

  return (
    <div className="flex min-h-screen" data-testid="app-container">
      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden" 
          onClick={() => setMobileMenuOpen(false)}
          data-testid="mobile-overlay"
        />
      )}
      {selectedProduct && activePanel !== 'reports' && activePanel !== 'myproducts' && activePanel !== 'changelog' && (
        <Sidebar 
          activePanel={activePanel} 
          onNavigate={handleNavigate} 
          collapsed={collapsed} 
          onToggle={() => setCollapsed((c) => !c)}
          selectedProduct={selectedProduct}
          isAdmin={isAdmin}
          user={user}
          mobileOpen={mobileMenuOpen}
          onMobileClose={() => setMobileMenuOpen(false)}
        />
      )}
      <main className={`flex-1 min-h-screen transition-all duration-300 ${selectedProduct && activePanel !== 'reports' && activePanel !== 'myproducts' ? (collapsed ? "md:ml-16" : "md:ml-64") : ""}`} style={{ background: "var(--theme-bg)" }}>
        <AppHeader onMobileMenuToggle={() => setMobileMenuOpen((o) => !o)} mobileMenuOpen={mobileMenuOpen} onNavigate={handleNavigate} onExport={() => setShowExportModal(true)} />
        <div key={activePanel} className="panel-enter p-4 sm:p-6 md:p-8 lg:p-12 max-w-7xl">
          <ActiveComponent {...componentProps} />
        </div>
      </main>

      {/* Notification Popup Modal */}
      <NotificationPopupModal onNavigate={(panel, productId) => {
        if (productId && !selectedProduct) {
          // If no product selected, try to select it first
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/my-products`).then((res) => {
            const prod = res.data.find((p) => p.id === productId);
            if (prod) {
              selectProduct(prod);
              setTimeout(() => setActivePanel(panel), 100);
            }
          });
        } else {
          handleNavigate(panel);
        }
      }} />

      {showExportModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" data-testid="export-modal-overlay">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6" data-testid="export-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center">
                  <FileDown className="w-5 h-5 text-[#00D4C8]" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800 font-['Outfit']">Export PRD</h3>
              </div>
              <button onClick={() => setShowExportModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="export-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed mb-6">
              Would you like to export this project application as a PRD? Click the options below to select either PDF or Word Document.
            </p>
            <div className="flex gap-3">
              <Button className="flex-1 bg-[#0F172A] hover:bg-slate-800 text-white" data-testid="export-pdf-btn"
                onClick={() => { window.open(`${process.env.REACT_APP_BACKEND_URL}/api/export/pdf`, "_blank"); setShowExportModal(false); }}>
                <FileDown className="w-4 h-4 mr-2" />PDF
              </Button>
              <Button className="flex-1 bg-[#0F172A] hover:bg-slate-800 text-white" data-testid="export-word-btn"
                onClick={() => { window.open(`${process.env.REACT_APP_BACKEND_URL}/api/export/word`, "_blank"); setShowExportModal(false); }}>
                <FileText className="w-4 h-4 mr-2" />Word Document
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  // Handle public share routes — no auth required
  if (window.location.pathname.startsWith("/share/quadchart/")) {
    return (
      <ThemeProvider>
        <SharedQuadChart />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <AuthProvider>
        <MainApp />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
